import type {BundledLanguage, ThemedToken} from "shiki";

/** Bundled grammars plus plaintext (`text`) for unknown execute_code languages. */
export type HighlightLanguage = BundledLanguage | "text";

export interface TokenizedCode {
   tokens: ThemedToken[][];
}

// Streaming snapshots each occupy an entry, so keep the cache bounded.
const MAX_TOKENS_CACHE_SIZE = 50;

type ShikiHighlighter = {
   getLoadedLanguages: () => string[];
   codeToTokens: (
      code: string,
      options: {
         lang: string;
         themes: {dark: string; light: string};
      }
   ) => {tokens: ThemedToken[][]};
};

let highlighterPromise: Promise<ShikiHighlighter> | null = null;

const tokensCache = new Map<string, TokenizedCode>();

const subscribers = new Map<string, Set<(result: TokenizedCode) => void>>();

/** In-flight highlight jobs keyed by cache key — coalesces StrictMode double-mounts. */
const inflight = new Map<string, Promise<TokenizedCode | null>>();

const getTokensCacheKey = (code: string, language: HighlightLanguage) =>
   `${language}:${code}`;

const readTokensCache = (key: string): TokenizedCode | null => {
   const cached = tokensCache.get(key);
   if (!cached) {
      return null;
   }
   // Refresh recency so eviction drops least-recently-used entries first.
   tokensCache.delete(key);
   tokensCache.set(key, cached);
   return cached;
};

const writeTokensCache = (key: string, value: TokenizedCode) => {
   tokensCache.delete(key);
   tokensCache.set(key, value);
   while (tokensCache.size > MAX_TOKENS_CACHE_SIZE) {
      const oldest = tokensCache.keys().next().value;
      if (oldest === undefined) {
         break;
      }
      tokensCache.delete(oldest);
   }
};

/**
 * Lazily loads Shiki core + grammars + themes into a separate chunk so the
 * chat bundle does not pay ~244KB minified up front for code blocks.
 */
const getHighlighter = (): Promise<ShikiHighlighter> => {
   highlighterPromise ??= (async () => {
      const [
         {createHighlighterCore},
         {createOnigurumaEngine},
         {default: langJson},
         {default: langPython},
         {default: langShellscript},
         {default: themeGithubDark},
         {default: themeGithubLight},
      ] = await Promise.all([
         import("shiki/core"),
         import("shiki/engine/oniguruma"),
         import("shiki/langs/json.mjs"),
         import("shiki/langs/python.mjs"),
         import("shiki/langs/shellscript.mjs"),
         import("shiki/themes/github-dark.mjs"),
         import("shiki/themes/github-light.mjs"),
      ]);

      return createHighlighterCore({
         langs: [langPython, langShellscript, langJson],
         themes: [themeGithubLight, themeGithubDark],
         engine: createOnigurumaEngine(() => import("shiki/wasm")),
      });
   })();
   return highlighterPromise;
};

/** Raw tokens for immediate display while highlighting loads. */
export const createRawTokens = (code: string): TokenizedCode => ({
   tokens: code.split("\n").map((line) =>
      line === ""
         ? []
         : [
              {
                 color: "inherit",
                 content: line,
              } as ThemedToken,
           ]
   ),
});

const tokenize = async (
   code: string,
   language: HighlightLanguage,
   tokensCacheKey: string
): Promise<TokenizedCode | null> => {
   try {
      const highlighter = await getHighlighter();
      const availableLangs = highlighter.getLoadedLanguages();
      const langToUse =
         language === "text" || !availableLangs.includes(language) ? "text" : language;

      const result = highlighter.codeToTokens(code, {
         lang: langToUse,
         themes: {
            dark: "github-dark",
            light: "github-light",
         },
      });

      const tokenized: TokenizedCode = {
         tokens: result.tokens,
      };

      writeTokensCache(tokensCacheKey, tokenized);
      return tokenized;
   } catch (error) {
      console.error("Failed to highlight code:", error);
      return null;
   }
};

/**
 * Returns cached tokens synchronously, or `null` while highlighting runs.
 * Pass a callback to receive async results — safe to call from an effect,
 * not during render (cold-path tokenization is async-only).
 */
export const highlightCode = (
   code: string,
   language: HighlightLanguage,
   // oxlint-disable-next-line eslint-plugin-promise(prefer-await-to-callbacks)
   callback?: (result: TokenizedCode) => void
): TokenizedCode | null => {
   const tokensCacheKey = getTokensCacheKey(code, language);

   const cached = readTokensCache(tokensCacheKey);
   if (cached) {
      return cached;
   }

   if (callback) {
      if (!subscribers.has(tokensCacheKey)) {
         subscribers.set(tokensCacheKey, new Set());
      }
      subscribers.get(tokensCacheKey)?.add(callback);
   }

   if (!inflight.has(tokensCacheKey)) {
      const job = tokenize(code, language, tokensCacheKey).then((tokenized) => {
         inflight.delete(tokensCacheKey);
         if (!tokenized) {
            subscribers.delete(tokensCacheKey);
            return null;
         }

         const subs = subscribers.get(tokensCacheKey);
         if (subs) {
            for (const sub of subs) {
               sub(tokenized);
            }
            subscribers.delete(tokensCacheKey);
         }
         return tokenized;
      });
      inflight.set(tokensCacheKey, job);
   }

   return null;
};
